import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Car, Loader2, Plus, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const emptyVehicle = {
  name: "",
  vehicle_number: "",
  vehicle_type: "car",
  capacity: 4,
  driver_name: "",
  driver_phone: "",
  color: "#1A362D",
  notes: "",
};

const emptyRide = {
  vehicle_id: "",
  direction: "arrival",
  date: "",
  time: "",
  location: "",
  notes: "",
};

const MODE_LABEL = { car: "Car", air: "Air", train: "Train", bus: "Bus" };

function seatsLabel(used, capacity) {
  return `${used}/${capacity || 0} seats`;
}

export default function Travel() {
  const { eventId } = useOutletContext();
  const [board, setBoard] = useState({ vehicles: [], rides: [], guests: [] });
  const [loading, setLoading] = useState(true);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [vehicleForm, setVehicleForm] = useState(emptyVehicle);
  const [vehicleId, setVehicleId] = useState(null);
  const [rideOpen, setRideOpen] = useState(false);
  const [rideForm, setRideForm] = useState(emptyRide);
  const [rideId, setRideId] = useState(null);
  const [assignRide, setAssignRide] = useState(null);
  const [assignGuestId, setAssignGuestId] = useState("");
  const [assignParty, setAssignParty] = useState("1");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/travel/${eventId}`);
      setBoard(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load travel plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) load();
  }, [eventId]);

  const saveVehicle = async () => {
    setSaving(true);
    try {
      const payload = {
        ...vehicleForm,
        capacity: Number(vehicleForm.capacity) || 1,
      };
      if (vehicleId) await api.put(`/vehicles/${eventId}/${vehicleId}`, payload);
      else await api.post(`/vehicles/${eventId}`, payload);
      toast.success(vehicleId ? "Vehicle updated" : "Vehicle added");
      setVehicleOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save vehicle");
    } finally {
      setSaving(false);
    }
  };

  const removeVehicle = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await api.delete(`/vehicles/${eventId}/${id}`);
      toast.success("Vehicle removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete vehicle");
    }
  };

  const saveRide = async () => {
    setSaving(true);
    try {
      if (rideId) await api.put(`/rides/${eventId}/${rideId}`, rideForm);
      else await api.post(`/rides/${eventId}`, rideForm);
      toast.success(rideId ? "Ride updated" : "Ride added");
      setRideOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save ride");
    } finally {
      setSaving(false);
    }
  };

  const removeRide = async (id) => {
    if (!window.confirm("Delete this ride and its passengers?")) return;
    try {
      await api.delete(`/rides/${eventId}/${id}`);
      toast.success("Ride removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete ride");
    }
  };

  const openAssign = (ride) => {
    setAssignRide(ride);
    setAssignGuestId("");
    setAssignParty("1");
  };

  const eligibleGuests = (board.guests || []).filter((g) =>
    !(assignRide?.assignments || []).some((a) => a.guest_id === g.id),
  );

  const saveAssign = async () => {
    const guest = (board.guests || []).find((g) => g.id === assignGuestId);
    if (!guest) return toast.error("Select a family whose timing matches this ride");
    setSaving(true);
    try {
      await api.post("/ride-assignments", {
        ride_id: assignRide.id,
        guest_id: guest.id,
        party_size: Number(assignParty) || 1,
      });
      toast.success("Family added to the ride");
      setAssignRide(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not assign");
    } finally {
      setSaving(false);
    }
  };

  const unassign = async (id) => {
    try {
      await api.delete(`/ride-assignments/${id}`);
      toast.success("Removed from ride");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove");
    }
  };

  const ridesFor = (vehicleIdValue) => (board.rides || []).filter((r) => r.vehicle_id === vehicleIdValue);

  return (
    <div data-testid="travel-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Travel</h1>
          <p className="text-sm text-[#525252]">
            Define each car, then place families on a ride when the vehicle has seats and their arrival or departure is within an hour.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setRideForm({ ...emptyRide, vehicle_id: board.vehicles[0]?.id || "" });
              setRideId(null);
              setRideOpen(true);
            }}
            disabled={!board.vehicles.length}
            data-testid="add-ride-button"
          >
            <Plus className="mr-2 h-4 w-4" /> Add ride
          </Button>
          <Button
            className="bg-[#1A362D] hover:bg-[#12271f]"
            onClick={() => {
              setVehicleForm(emptyVehicle);
              setVehicleId(null);
              setVehicleOpen(true);
            }}
            data-testid="add-vehicle-button"
          >
            <Plus className="mr-2 h-4 w-4" /> Add vehicle
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
        </div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[280px_1fr]">
          <aside className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[#525252]">Accepted guests</h2>
            {(board.guests || []).length === 0 && (
              <p className="rounded-md border border-[#E5E5E5] bg-white p-4 text-sm text-[#525252]">
                Travel plans appear here after a guest accepts and shares how they are arriving.
              </p>
            )}
            {(board.guests || []).map((g) => {
              const travel = g.travel || {};
              return (
                <div key={g.id} className="rounded-md border border-[#E5E5E5] bg-white p-3" data-testid={`travel-guest-${g.id}`}>
                  <div className="font-medium">{g.primary_guest_name}</div>
                  <div className="text-xs text-[#525252]">{g.family_name} · {g.party_size} travelling</div>
                  <div className="mt-2 text-xs leading-relaxed text-[#44403C]">
                    {travel.arrival_mode ? MODE_LABEL[travel.arrival_mode] || travel.arrival_mode : "Arrival not shared"}
                    {travel.arrival_date ? ` · ${travel.arrival_date}` : ""}
                    {travel.arrival_time ? ` ${travel.arrival_time}` : ""}
                    {travel.arrival_details ? ` · ${travel.arrival_details}` : ""}
                  </div>
                  <div className="mt-1 text-xs leading-relaxed text-[#44403C]">
                    Drop-off {travel.departure_date || "date pending"}
                    {travel.departure_time ? ` ${travel.departure_time}` : ""}
                    {travel.dropoff_location ? ` · ${travel.dropoff_location}` : ""}
                  </div>
                </div>
              );
            })}
          </aside>

          <div className="grid gap-4 md:grid-cols-2">
            {(board.vehicles || []).length === 0 && (
              <p className="rounded-md border border-dashed border-[#E5E5E5] bg-white p-8 text-sm text-[#525252]">
                Add a vehicle with its number, driver, and seat count. Families are matched onto that car visually.
              </p>
            )}
            {(board.vehicles || []).map((vehicle) => (
              <article
                key={vehicle.id}
                className="overflow-hidden rounded-lg border border-[#E5E5E5] bg-white"
                data-testid={`vehicle-card-${vehicle.id}`}
              >
                <header className="flex items-start justify-between gap-3 border-b border-[#E5E5E5] p-4" style={{ borderTop: `6px solid ${vehicle.color || "#1A362D"}` }}>
                  <div>
                    <div className="flex items-center gap-2 font-semibold">
                      <Car className="h-4 w-4" style={{ color: vehicle.color || "#1A362D" }} />
                      {vehicle.name}
                    </div>
                    <p className="mt-1 text-xs text-[#525252]">
                      {[vehicle.vehicle_number, vehicle.vehicle_type, `${vehicle.capacity} seats`, vehicle.driver_name, vehicle.driver_phone].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setVehicleForm({ ...emptyVehicle, ...vehicle });
                        setVehicleId(vehicle.id);
                        setVehicleOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => removeVehicle(vehicle.id)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </header>
                <div className="space-y-3 p-4">
                  {ridesFor(vehicle.id).length === 0 && (
                    <p className="text-sm text-[#78716C]">No rides yet.</p>
                  )}
                  {ridesFor(vehicle.id).map((ride) => (
                    <div key={ride.id} className="rounded-md border border-[#E5E5E5] p-3" data-testid={`ride-card-${ride.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium capitalize">{ride.direction} · {ride.date} {ride.time}</p>
                          <p className="text-xs text-[#525252]">{ride.location || "Location not set"} · {seatsLabel(ride.seats_used, ride.capacity)}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => openAssign(ride)} data-testid={`add-passenger-${ride.id}`}>
                            <Users className="mr-1 h-3.5 w-3.5" /> Add
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => {
                            setRideForm({
                              vehicle_id: ride.vehicle_id,
                              direction: ride.direction,
                              date: ride.date,
                              time: ride.time,
                              location: ride.location || "",
                              notes: ride.notes || "",
                            });
                            setRideId(ride.id);
                            setRideOpen(true);
                          }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => removeRide(ride.id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#F4EFE6]">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.min(100, ((ride.seats_used || 0) / (ride.capacity || 1)) * 100)}%`,
                            background: vehicle.color || "#1A362D",
                          }}
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(ride.assignments || []).map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => unassign(a.id)}
                            title="Remove from this ride"
                            className="rounded-full px-3 py-1 text-xs text-white"
                            style={{ background: vehicle.color || "#1A362D" }}
                            data-testid={`ride-family-${a.id}`}
                          >
                            {a.guest_name || "Guest"} · {a.party_size}
                            {a.family_name ? ` · ${a.family_name}` : ""}
                          </button>
                        ))}
                        {(ride.assignments || []).length === 0 && (
                          <span className="text-xs text-[#78716C]">No families matched yet.</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      <Dialog open={vehicleOpen} onOpenChange={setVehicleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{vehicleId ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label>Name</Label>
              <Input value={vehicleForm.name} onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value })} data-testid="vehicle-name" />
            </div>
            <div className="space-y-1">
              <Label>Number</Label>
              <Input value={vehicleForm.vehicle_number} onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_number: e.target.value })} data-testid="vehicle-number" />
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={vehicleForm.vehicle_type} onValueChange={(v) => setVehicleForm({ ...vehicleForm, vehicle_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="car">Car</SelectItem>
                  <SelectItem value="suv">SUV</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="bus">Bus</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Capacity</Label>
              <Input type="number" min="1" value={vehicleForm.capacity} onChange={(e) => setVehicleForm({ ...vehicleForm, capacity: e.target.value })} data-testid="vehicle-capacity" />
            </div>
            <div className="space-y-1">
              <Label>Colour</Label>
              <Input type="color" value={vehicleForm.color || "#1A362D"} onChange={(e) => setVehicleForm({ ...vehicleForm, color: e.target.value })} className="h-10 p-1" />
            </div>
            <div className="space-y-1">
              <Label>Driver</Label>
              <Input value={vehicleForm.driver_name} onChange={(e) => setVehicleForm({ ...vehicleForm, driver_name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Driver phone</Label>
              <Input value={vehicleForm.driver_phone} onChange={(e) => setVehicleForm({ ...vehicleForm, driver_phone: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveVehicle} disabled={saving || !vehicleForm.name} className="bg-[#1A362D]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rideOpen} onOpenChange={setRideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{rideId ? "Edit ride" : "Add ride"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Vehicle</Label>
              <Select value={rideForm.vehicle_id} onValueChange={(v) => setRideForm({ ...rideForm, vehicle_id: v })}>
                <SelectTrigger data-testid="ride-vehicle"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>
                  {(board.vehicles || []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name} · {v.capacity} seats</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Direction</Label>
              <Select value={rideForm.direction} onValueChange={(v) => setRideForm({ ...rideForm, direction: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="arrival">Arrival pickup</SelectItem>
                  <SelectItem value="departure">Departure drop-off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Date</Label>
                <Input type="date" value={rideForm.date} onChange={(e) => setRideForm({ ...rideForm, date: e.target.value })} data-testid="ride-date" />
              </div>
              <div className="space-y-1">
                <Label>Time</Label>
                <Input type="time" value={rideForm.time} onChange={(e) => setRideForm({ ...rideForm, time: e.target.value })} data-testid="ride-time" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Pickup or drop-off place</Label>
              <Input value={rideForm.location} onChange={(e) => setRideForm({ ...rideForm, location: e.target.value })} placeholder="Airport, station, hotel gate…" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveRide} disabled={saving || !rideForm.vehicle_id} className="bg-[#1A362D]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignRide} onOpenChange={(open) => !open && setAssignRide(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Match a family</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#525252]">
            {assignRide ? `${assignRide.seats_remaining} seat${assignRide.seats_remaining === 1 ? "" : "s"} left. Accepted families are listed. A time note appears when their ${assignRide.direction} is within an hour of this ride.` : ""}
          </p>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Family</Label>
              <Select
                value={assignGuestId}
                onValueChange={(id) => {
                  setAssignGuestId(id);
                  const guest = eligibleGuests.find((g) => g.id === id);
                  const room = Math.min(guest?.party_size || 1, assignRide?.seats_remaining || 1);
                  setAssignParty(String(Math.max(1, room)));
                }}
              >
                <SelectTrigger data-testid="ride-guest"><SelectValue placeholder="Select a family" /></SelectTrigger>
                <SelectContent>
                  {eligibleGuests.map((g) => {
                    const close = (g.suggested_rides || []).some((s) => s.ride_id === assignRide?.id);
                    return (
                      <SelectItem key={g.id} value={g.id}>
                        {g.primary_guest_name}{g.family_name ? ` (${g.family_name})` : ""} · {g.party_size}{close ? " · time matches" : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {eligibleGuests.length === 0 && (
                <p className="text-xs text-[#78716C]">No accepted guests are waiting for this ride.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Seats for this family</Label>
              <Input type="number" min="1" value={assignParty} onChange={(e) => setAssignParty(e.target.value)} data-testid="ride-party-size" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveAssign} disabled={saving} className="bg-[#1A362D]">Add to ride</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
